import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { type RowData, useTable } from "@tanstack/react-table";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { readEditorValue } from "./editing/cellAccess";
import { createUndoStack } from "./editing/undoStack";
import { useRangeChangeCallback } from "./features/range/useRangeChangeCallback";
import { useRangeReset } from "./features/range/useRangeReset";
import { withDraftRowOpenGuard } from "./features/draftRows";
import { useRowKinds } from "./features/rowKinds";
import { useLoadMoreState } from "./features/loadMore/useLoadMoreState";
import { useClientPageReset } from "./features/pagination/useClientPageReset";
import { useAllMatchingSelection } from "./features/selection/useAllMatchingSelection";
import { useInstanceFlags } from "./instanceFlags";
import { useAddedColumnPins } from "./layout/addedColumnPins";
import type {
    DataGridStatus,
    DataGridInstance,
    UseDataGridOptions,
} from "./types";
import {
    buildExternalAtoms,
    buildInitialState,
    buildTableOptions,
    getCapabilities,
    getServerPageCount,
    getServerRowCount,
    selectRootState,
} from "./tableOptions";
import {
    useColumnStateSync,
    useDefaultColumnState,
} from "./useColumnStateSync";
import { useConstant } from "@hooks/useConstant";
import { useControlledSlices } from "./useControlledSlices";
import { useGridAtoms } from "./useGridAtoms";
import { useStableStatus } from "./useStableStatus";
import { useTotals } from "./useTotals";

const EMPTY_DATA: readonly never[] = [];
const READY_STATUS: DataGridStatus = { state: "ready" };

/** A gate keeps hidden rows out of the table, so sorting can't reveal them. */
const useGatedData = <TData extends RowData>(
    data: readonly TData[],
    visibleRowCount: number | undefined,
) =>
    useMemo(
        () =>
            visibleRowCount === undefined
                ? data
                : data.slice(0, visibleRowCount),
        [data, visibleRowCount],
    );

/**
 * Builds a DataGrid instance: TanStack table, status, density, and the
 * table-owned atoms. Pass the result to `<DataGrid table={...}>`.
 *
 * Capabilities (`rowSelection`, `sorting`, `expanding`, ...) and how each
 * slice is owned (internal, controlled, or atom) are fixed for the table's
 * lifetime. `columns` must be stable; `data` may change freely.
 */
export const useDataGrid = <TData extends RowData>(
    options: UseDataGridOptions<TData>,
): DataGridInstance<TData> => {
    const optionsRef = useRef(options);
    const rowKinds = useRowKinds(optionsRef);
    useLayoutEffect(() => {
        optionsRef.current = withDraftRowOpenGuard({
            options,
            isDraft: rowKinds.isDraft,
        });
    });

    const {
        capabilities,
        externalAtoms,
        initialState,
        hasSubRows,
        hasSelectableFilter,
        virtualization,
    } = useConstant(() => ({
        capabilities: getCapabilities(options),
        externalAtoms: buildExternalAtoms(options),
        initialState: buildInitialState(options),
        hasSubRows: Boolean(options.expanding?.getSubRows),
        hasSelectableFilter: Boolean(
            options.rowSelection?.isRowSelectable ??
            options.separatorRows ??
            (options.rowSelection && options.draftRows),
        ),
        virtualization: options.virtualization,
    }));

    const getRowId = useCallback(
        (row: TData, index: number) => optionsRef.current.getRowId(row, index),
        [],
    );
    const getSubRows = useCallback(
        (row: TData) => optionsRef.current.expanding?.getSubRows?.(row),
        [],
    );
    const isRowSelectable = useCallback(
        (row: TData) =>
            !rowKinds.isSeparator(row) &&
            !rowKinds.isDraft(row) &&
            (optionsRef.current.rowSelection?.isRowSelectable?.(row) ?? true),
        [rowKinds],
    );

    const { controlledState, changeHandlers } = useControlledSlices(options);

    const gateRowCount = options.gate?.visibleRowCount;
    const allData = options.data ?? EMPTY_DATA;
    const isGateHoldingRows =
        gateRowCount !== undefined && allData.length > gateRowCount;
    const gate = useMemo(
        () =>
            isGateHoldingRows && gateRowCount !== undefined
                ? { visibleRowCount: gateRowCount }
                : undefined,
        [isGateHoldingRows, gateRowCount],
    );
    const data = useGatedData(allData, gateRowCount);
    const { columns, meta } = options;
    const serverRowCount = getServerRowCount(options);
    const serverPageCount = getServerPageCount(options);
    const tableOptions = useMemo(
        () =>
            buildTableOptions<TData>({
                data,
                columns,
                getRowId,
                getSubRows: hasSubRows ? getSubRows : undefined,
                isRowSelectable: hasSelectableFilter
                    ? isRowSelectable
                    : undefined,
                controlledState,
                changeHandlers,
                externalAtoms,
                initialState,
                capabilities,
                serverRowCount,
                serverPageCount,
                meta,
            }),
        [
            data,
            columns,
            getRowId,
            hasSubRows,
            getSubRows,
            hasSelectableFilter,
            isRowSelectable,
            controlledState,
            changeHandlers,
            externalAtoms,
            initialState,
            capabilities,
            serverRowCount,
            serverPageCount,
            meta,
        ],
    );
    const table = useTable(tableOptions, selectRootState);
    useClientPageReset(table);

    const allMatchingSelected = useAllMatchingSelection({
        table,
        data,
        resetKey: options.loadMore?.resetKey,
        optionsRef,
    });
    const atoms = useGridAtoms({ allMatchingSelected, options });

    const undoStack = useConstant(createUndoStack);
    const tableRef = useLatestRef(table);
    useLayoutEffect(() => {
        undoStack.onDataChange((address) =>
            readEditorValue(tableRef.current, address),
        );
    }, [undoStack, data, tableRef]);
    useRangeReset(table);
    useRangeChangeCallback({
        table,
        optionsRef,
        hasListener: options.rangeSelection?.onChange !== undefined,
    });

    useColumnStateSync({
        table,
        columnVariants: atoms.columnVariants,
        columnState: options.columnState,
        optionsRef,
    });
    useAddedColumnPins({ table, columns });
    const defaultColumnState = useDefaultColumnState({
        columns,
        declared: options.defaultColumnState,
    });

    const status = useStableStatus(options.status ?? READY_STATUS);
    const flags = useInstanceFlags({ options, capabilities });
    const {
        globalFilter: globalFilterValue,
        columnFilters: columnFiltersValue,
    } = table.state;
    const hasActiveFilters =
        Boolean(globalFilterValue) || columnFiltersValue.length > 0;
    const loadMore = useLoadMoreState(options.loadMore);
    const totalRowCount =
        serverRowCount ??
        loadMore?.rowCount ??
        table.getPrePaginatedRowModel().rows.length;
    const hasMoreRows = loadMore?.hasMore ?? false;
    const totals = useTotals({
        values: options.totals,
        isPartial: capabilities.isServerPagination || hasMoreRows,
    });

    return useMemo(
        () => ({
            ...flags,
            table,
            status,
            undoStack,
            totals,
            hasActiveFilters,
            totalRowCount,
            hasMoreRows,
            loadMore,
            gate,
            atoms,
            rowKinds,
            virtualization,
            defaultColumnState,
            optionsRef,
        }),
        [
            flags,
            table,
            status,
            undoStack,
            totals,
            hasActiveFilters,
            totalRowCount,
            hasMoreRows,
            loadMore,
            gate,
            atoms,
            rowKinds,
            virtualization,
            defaultColumnState,
        ],
    );
};
